import React, { useEffect, useMemo, useState } from 'react';
import { fieldDomId, fieldProps } from './missingFields';
import Icon from '../../../components/AppIcon';
import { StepHeader } from './StepHeader';
import { isIndia, getCountryName } from '../../../utils/countries';
import { distinctLegalName } from '../../../utils/vendorName';

/* ------------------------------------------------------------------ helpers */

const DASH = '—';

/** Trimmed string, or null when the value is empty — the single "is this filled?" test. */
const text = (v) => {
  if (v === null || v === undefined || typeof v === 'boolean') return null;
  const s = String(v).trim();
  return s === '' ? null : s;
};

const labelOf = (options, value) => options.find((o) => o.value === value)?.label ?? text(value);

const titleCase = (slug) =>
  String(slug || '')
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

const formatDate = (value) => {
  const raw = text(value);
  if (!raw) return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatMoney = (value, currency = 'INR') => {
  const n = Number(value);
  if (!Number.isFinite(n) || String(value ?? '').trim() === '') return null;
  try {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency, maximumFractionDigits: 0 }).format(n);
  } catch {
    return `${currency} ${n.toLocaleString('en-IN')}`;
  }
};

/** Keep the last `keep` characters, mask the rest — used for account numbers and PAN. */
const maskTail = (value, keep = 4) => {
  const s = text(value);
  if (!s) return null;
  if (s.length <= keep) return s;
  return `${'•'.repeat(Math.min(s.length - keep, 12))}${s.slice(-keep)}`;
};

const ACCOUNT_TYPES = [
  { value: 'savings', label: 'Savings account' },
  { value: 'current', label: 'Current account' },
  { value: 'od', label: 'Overdraft account' },
  { value: 'cash-credit', label: 'Cash credit account' },
];
const MSME_STATUS = [
  { value: 'registered', label: 'MSME registered' },
  { value: 'not-registered', label: 'Not MSME registered' },
  { value: 'pending', label: 'Registration pending' },
];
const MSME_CATEGORIES = [
  { value: 'micro', label: 'Micro' },
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
];
const VENDOR_TYPES = [
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'trading', label: 'Trading' },
  { value: 'service', label: 'Service' },
];
const ASSESSEE = [
  { value: 'individual', label: 'Individual' },
  { value: 'huf', label: 'HUF' },
  { value: 'company', label: 'Company' },
  { value: 'firm', label: 'Firm' },
  { value: 'aop', label: 'Association of Persons' },
  { value: 'body-of-individuals', label: 'Body of Individuals' },
  { value: 'local-authority', label: 'Local Authority' },
  { value: 'artificial-juridical-person', label: 'Artificial Juridical Person' },
  { value: 'trust', label: 'Trust' },
];
const BANK_METHOD = {
  typed: 'Confirmed with your bank',
  cheque_ocr: 'Read from your cancelled cheque, confirmed with your bank',
  upi_rpd: 'Confirmed by a ₹1 UPI verification',
};

/** Every agreement, with the same show-rules the validator uses. */
const AGREEMENTS = [
  { id: 'nda', title: 'Mutual Non-Disclosure Agreement (NDA)', shows: () => true },
  { id: 'sqa', title: 'Supplier Agreement', shows: (f) => isIndia(f.countryOrigin) },
  { id: 'fourM', title: '4M Change Control Agreement', shows: () => true },
  { id: 'codeOfConduct', title: 'Code of Conduct for Business Partners', shows: () => true },
  { id: 'complianceAgreement', title: 'Compliance Agreement', shows: (f) => isIndia(f.countryOrigin) },
  { id: 'selfDeclaration', title: 'Self Declaration', shows: () => true },
  { id: 'overseasAgreement', title: 'Overseas Agreement', shows: (f) => !isIndia(f.countryOrigin) },
];

/** Documents, in the order they are collected, with the step that owns each one. */
const DOCUMENTS = [
  { key: 'panDocument', type: 'PAN_CARD', label: 'PAN card', step: 'compliance', shows: (f) => isIndia(f.countryOrigin), required: true },
  { key: 'gstDocument', type: 'GST_CERTIFICATE', label: 'GST certificate', step: 'compliance', shows: (f) => isIndia(f.countryOrigin), required: true },
  { key: 'incorporationCertificate', type: 'INCORPORATION_CERTIFICATE', label: 'Certificate of incorporation', step: 'company', shows: (f) => !isIndia(f.countryOrigin), required: true },
  { key: 'bankProof', type: 'BANK_PROOF', label: 'Cancelled cheque / bank proof', step: 'bank', shows: () => true, required: true },
  { key: 'msmeCertificate', type: 'MSME_CERTIFICATE', label: 'Udyam (MSME) certificate', step: 'msme', shows: (f) => f.msmeStatus === 'registered', required: true },
  { key: 'msmeDeclarationFile', type: 'MSME_DECLARATION', label: 'MSME declaration', step: 'msme', shows: (f) => f.msmeStatus === 'not-registered', required: true },
  { key: 'digitalSignature', type: 'DIGITAL_SIGNATURE', label: 'Digital signature', step: 'company', shows: () => true, required: true },
];

/** Which section an error belongs to, so "needs attention" can link straight there. */
const FIELD_STEP = {
  countryOrigin: 'identity', gstinVerified: 'identity', emailVerified: 'identity',
  businessVertical: 'company', companyName: 'company', legalName: 'company', tradeName: 'company', contactPersonName: 'company',
  designation: 'company', email: 'company', phoneNumber: 'company', website: 'company',
  directorName: 'company', directorEmail: 'company', altSpocName: 'company', altSpocEmail: 'company', altSpocDesignation: 'company', altSpocPhone: 'company',
  yearEstablished: 'company', businessDescription: 'company', incorporationCertificate: 'company',
  registeredAddress: 'address', registeredCity: 'address', registeredState: 'address', registeredDistrict: 'address',
  registeredCountry: 'address', registeredPincode: 'address', supplyAddress: 'address', supplyCity: 'address',
  supplyState: 'address', supplyDistrict: 'address', supplyCountry: 'address', supplyPincode: 'address',
  bankName: 'bank', branchName: 'bank', accountNumber: 'bank', confirmAccountNumber: 'bank', accountType: 'bank',
  ifscCode: 'bank', swiftCode: 'bank', bankAddress: 'bank', bankProof: 'bank', bankVerified: 'bank',
  accountHolderName: 'bank',
  annualTurnover: 'msme', msmeStatus: 'msme', msmeCategory: 'msme', msmeNumber: 'msme', msmeCertificate: 'msme',
  msmeDeclaration: 'msme', msmeDeclarationFile: 'msme', classificationYear: 'msme', classificationDate: 'msme',
  vendorType: 'msme', industrySector: 'msme', employeeCount: 'msme', udyamVerified: 'msme', gstTurnoverSlab: 'msme',
  panNumber: 'compliance', gstNumber: 'compliance', natureOfAssessee: 'compliance', cinNumber: 'compliance',
  taxRegistrationNumber: 'compliance', vatNumber: 'compliance', businessLicense: 'compliance',
  gtaRegistration: 'compliance', preferredCurrency: 'compliance', panDocument: 'compliance', gstDocument: 'compliance',
  digitalSignature: 'company', agreementDownloads: 'agreements',
};

/* --------------------------------------------------------------- primitives */

/** One label/value pair. `verified` renders the same shield the locked inputs use. */
const Field = ({ label, value, hint, mono = false, verified = false, wide = false, children }) => {
  const shown = children ?? text(value);
  const empty = shown === null || shown === undefined;
  return (
    <div className={wide ? 'sm:col-span-2' : undefined}>
      <dt className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">{label}</dt>
      <dd
        className={`mt-1 flex items-start gap-1.5 text-[15px] leading-snug ${
          empty ? 'text-muted-foreground/70' : 'font-medium text-foreground'
        } ${mono ? 'font-mono tracking-tight' : ''}`}
      >
        <span className="min-w-0 break-words">{empty ? DASH : shown}</span>
        {verified && !empty && (
          <Icon name="ShieldCheck" size={14} className="mt-0.5 flex-shrink-0 text-success" aria-label="Verified from government records" />
        )}
      </dd>
      {hint && !empty && <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
};

const Section = ({ id, icon, title, meta, stepId, onEdit, flagged = false, children }) => (
  <section
    id={`review-${id}`}
    aria-labelledby={`review-${id}-title`}
    className={`scroll-mt-24 overflow-hidden rounded-xl border bg-card shadow-xs transition-colors ${
      flagged ? 'border-destructive/40' : 'border-border'
    }`}
  >
    <header className="flex items-center gap-3 border-b border-border bg-muted/40 px-4 py-3 sm:px-5">
      <span
        className={`grid h-9 w-9 flex-shrink-0 place-items-center rounded-lg ${
          flagged ? 'bg-destructive/10 text-destructive' : 'bg-primary/10 text-primary'
        }`}
      >
        <Icon name={icon} size={18} />
      </span>
      <div className="min-w-0 flex-1">
        <h3 id={`review-${id}-title`} className="text-sm font-semibold text-foreground">
          {title}
        </h3>
        {meta && <p className="truncate text-xs text-muted-foreground">{meta}</p>}
      </div>
      {stepId && (
        <button
          type="button"
          onClick={() => onEdit(stepId)}
          className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1.5 text-xs font-medium text-foreground transition-colors hover:border-primary/40 hover:bg-primary/5 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        >
          <Icon name="Pencil" size={13} />
          Edit
        </button>
      )}
    </header>
    <dl className="grid grid-cols-1 gap-x-6 gap-y-5 px-4 py-5 sm:grid-cols-2 sm:px-5">{children}</dl>
  </section>
);

const Badge = ({ icon, children, tone = 'success' }) => {
  const tones = {
    success: 'border-success/30 bg-success/10 text-success',
    muted: 'border-border bg-muted text-muted-foreground',
    warning: 'border-warning/40 bg-warning/10 text-foreground',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${tones[tone]}`}>
      {icon && <Icon name={icon} size={13} />}
      {children}
    </span>
  );
};

/* -------------------------------------------------------------------- step */

const ReviewStep = ({
  formData,
  updateFormData,
  errors = {},
  issues = {},
  plan = [],
  onEditStep,
  identity,
  kycActive = false,
  locked,
  lockSources,
  uploadedFiles = {},
  keepExistingDocuments = {},
  onPreviewDocument,
  emailVerified = false,
}) => {
  const india = isIndia(formData.countryOrigin);
  const currency = formData.preferredCurrency || (india ? 'INR' : 'USD');
  const stepIds = useMemo(() => new Set(plan.map((s) => s.id)), [plan]);
  const has = (id) => stepIds.has(id);
  const msmeStepId = has('msme') ? 'msme' : 'categorization';

  // Locked keys tell us which values came from a registry, so the summary can show provenance.
  const lockedKeys = useMemo(() => {
    const out = new Set();
    Object.values(locked || {}).forEach((rec) => Object.keys(rec || {}).forEach((k) => out.add(k)));
    return out;
  }, [locked]);
  const isVerified = (key) => lockedKeys.has(key);

  const [revealed, setRevealed] = useState({ account: false, pan: false });
  const toggle = (key) => setRevealed((prev) => ({ ...prev, [key]: !prev[key] }));

  // Signature preview (only possible for a file still held in the browser)
  const [signatureUrl, setSignatureUrl] = useState(null);
  useEffect(() => {
    const file = formData.digitalSignature;
    if (file instanceof File && file.type?.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      setSignatureUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setSignatureUrl(null);
    return undefined;
  }, [formData.digitalSignature]);

  const documents = DOCUMENTS.filter((d) => d.shows(formData) && (d.step !== 'compliance' || has('compliance') || india)).map((d) => {
    const file = formData[d.key];
    const staged = uploadedFiles[d.type];
    const keeping = keepExistingDocuments[d.key];
    const name = (file instanceof File && file.name) || staged?.file_name || (keeping ? 'Existing document kept' : null);
    return {
      ...d,
      name,
      present: Boolean(name),
      previewable: file instanceof File || Boolean(staged?.id) || Boolean(keeping),
      source: file instanceof File ? 'You uploaded this' : staged ? 'Saved to your application' : keeping ? 'Kept from your existing profile' : null,
      handle: file instanceof File ? file : staged || null,
    };
  });

  const agreements = AGREEMENTS.filter((a) => a.shows(formData));
  const acceptedCount = agreements.filter((a) => formData.agreements?.[a.id]).length;

  // Anything still blocking submission, grouped by the section that can fix it.
  const outstanding = useMemo(() => {
    const bySection = new Map();
    Object.entries(issues || {}).forEach(([field, message]) => {
      if (field === 'finalConfirmation') return;
      const stepId = field.startsWith('agreements.') ? 'agreements' : FIELD_STEP[field] || 'company';
      const target = stepIds.has(stepId) ? stepId : msmeStepId;
      if (!bySection.has(target)) bySection.set(target, []);
      bySection.get(target).push(typeof message === 'string' ? message : 'Needs attention');
    });
    return [...bySection.entries()].map(([stepId, messages]) => ({ stepId, messages }));
  }, [issues, stepIds, msmeStepId]);
  const flaggedSteps = new Set(outstanding.map((o) => o.stepId));

  const SECTION_LINKS = [
    { id: 'business', label: 'Business' },
    { id: 'address', label: 'Address' },
    { id: 'bank', label: 'Bank' },
    { id: msmeStepId === 'msme' ? 'msme' : 'categorization', label: india ? 'MSME' : 'Categorization' },
    ...(has('compliance') ? [{ id: 'compliance', label: 'Compliance' }] : []),
    { id: 'documents', label: 'Documents' },
    { id: 'agreements', label: 'Agreements' },
  ];
  const jumpTo = (id) => document.getElementById(`review-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const bank = identity?.bank || null;
  const udyam = identity?.udyam || null;
  const confirmed = Boolean(formData.finalConfirmation);

  return (
    <div className="space-y-6">
      <StepHeader
        eyebrow="Last step"
        title="Review & submit"
        description="Here is everything you have given us. Check it once — you can jump back to any section to change something before you send it."
      />

      {/* ---------------------------------------------------------- hero */}
      <div className="rounded-xl border border-border bg-gradient-to-br from-primary/[0.07] via-card to-card p-5 shadow-xs sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-primary">Applying as</p>
            <h3 className="mt-1 break-words text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
              {text(formData.companyName) || 'Your business'}
            </h3>
            {distinctLegalName(formData.companyName, formData.legalName) && (
              <p className="mt-1 text-sm text-muted-foreground">Legal name: {distinctLegalName(formData.companyName, formData.legalName)}</p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {text(formData.countryOrigin) && (
              <Badge icon="Globe" tone="muted">{getCountryName(formData.countryOrigin) || formData.countryOrigin}</Badge>
            )}
            {text(formData.gstNumber) && <Badge icon="FileCheck" tone="muted">GSTIN {formData.gstNumber}</Badge>}
          </div>
        </div>

        {(kycActive || emailVerified) && (
          <div className="mt-5 border-t border-border pt-4">
            <div className="flex flex-wrap gap-2">
              {emailVerified && <Badge icon="MailCheck">Email verified</Badge>}
              {identity?.verified_at && <Badge icon="ShieldCheck">GSTIN verified with GSTN</Badge>}
              {identity?.mca?.cin && <Badge icon="Building2">Company details from MCA</Badge>}
              {bank?.verified_at && <Badge icon="Landmark">Bank account verified</Badge>}
              {identity?.udyam_verified_at && <Badge icon="Factory">Udyam verified</Badge>}
              {!identity?.verified_at && identity?.gstin_manual && <Badge icon="ShieldAlert" tone="warning">GSTIN entered manually — verified before approval</Badge>}
              {!bank?.verified_at && identity?.bank_manual && <Badge icon="ShieldAlert" tone="warning">Bank details entered manually — verified before approval</Badge>}
              {!identity?.udyam_verified_at && identity?.udyam_manual && <Badge icon="ShieldAlert" tone="warning">Udyam number entered manually — verified before approval</Badge>}
            </div>
            {lockedKeys.size > 0 && (
              <p className="mt-3 flex items-start gap-1.5 text-xs text-muted-foreground">
                <Icon name="ShieldCheck" size={13} className="mt-0.5 flex-shrink-0 text-success" />
                Values marked with this shield came straight from government records, so they could not be edited.
              </p>
            )}
          </div>
        )}
      </div>

      {/* ------------------------------------------------- needs attention */}
      {outstanding.length > 0 && (
        <div id="review-outstanding" className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <Icon name="AlertTriangle" size={20} className="mt-0.5 flex-shrink-0 text-destructive" />
            <div className="min-w-0 flex-1">
              <h3 className="text-sm font-semibold text-destructive">
                {outstanding.reduce((n, o) => n + o.messages.length, 0) === 1
                  ? 'One thing still needs your attention'
                  : `${outstanding.reduce((n, o) => n + o.messages.length, 0)} things still need your attention`}
              </h3>
              <ul className="mt-2 space-y-2">
                {outstanding.map(({ stepId, messages }) => (
                  <li key={stepId} className="flex flex-wrap items-baseline gap-x-2 text-sm text-foreground">
                    <span className="text-muted-foreground">{messages.join(' · ')}</span>
                    <button
                      type="button"
                      onClick={() => onEditStep(stepId)}
                      className="font-medium text-destructive underline underline-offset-2 hover:no-underline"
                    >
                      Fix in {plan.find((s) => s.id === stepId)?.label || 'this section'}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* -------------------------------------------------------- jump bar */}
      <nav className="flex flex-wrap items-center gap-2" aria-label="Jump to a section">
        <span className="text-xs font-medium text-muted-foreground">Jump to</span>
        {SECTION_LINKS.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => jumpTo(s.id)}
            className="rounded-full border border-border bg-card px-3 py-1 text-xs font-medium text-muted-foreground transition-colors hover:border-primary/40 hover:bg-primary/5 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          >
            {s.label}
          </button>
        ))}
      </nav>

      {/* -------------------------------------------------------- sections */}
      <Section
        id="business"
        icon="Building2"
        title="Business & contact"
        meta={titleCase(formData.businessVertical) || undefined}
        stepId={has('company') ? 'company' : null}
        onEdit={onEditStep}
        flagged={flaggedSteps.has('company')}
      >
        <Field label="Company name" value={formData.companyName} verified={isVerified('companyName')} />
        <Field label="Legal name" value={formData.legalName} verified={isVerified('legalName')} />
        <Field label="Country of origin" value={getCountryName(formData.countryOrigin) || formData.countryOrigin} verified={isVerified('countryOrigin')} />
        <Field label="Year established" value={formData.yearEstablished} verified={isVerified('yearEstablished')} />
        <Field label="Name of the director" value={formData.directorName} />
        <Field label="Director email" value={formData.directorEmail} />
        <Field label="Website" value={formData.website} />
        <Field label="What the business does" value={formData.businessDescription} wide />
        <Field label="SPOC name" value={formData.contactPersonName} />
        <Field label="SPOC designation" value={formData.designation} />
        <Field label="SPOC email" value={formData.email}>
          {text(formData.email) ? (
            <span className="inline-flex items-center gap-1.5">
              <span className="break-all">{formData.email}</span>
              {emailVerified && <Icon name="MailCheck" size={14} className="flex-shrink-0 text-success" aria-label="Verified" />}
            </span>
          ) : null}
        </Field>
        <Field label="SPOC phone" value={formData.phoneNumber} />
        <Field label="Alternate SPOC name" value={formData.altSpocName} />
        <Field label="Alternate SPOC designation" value={formData.altSpocDesignation} />
        <Field label="Alternate SPOC email" value={formData.altSpocEmail} />
        <Field label="Alternate SPOC phone" value={formData.altSpocPhone} />
      </Section>

      <Section
        id="address"
        icon="MapPin"
        title="Addresses"
        meta={formData.sameAsRegistered ? 'Supply address same as registered' : undefined}
        stepId={has('address') ? 'address' : null}
        onEdit={onEditStep}
        flagged={flaggedSteps.has('address')}
      >
        <div className="sm:col-span-2">
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.08em] text-primary">Registered office</p>
          <dl className="grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2">
            <Field label="Address" value={formData.registeredAddress} verified={isVerified('registeredAddress')} wide />
            <Field label="City" value={formData.registeredCity} />
            <Field label="District" value={formData.registeredDistrict} />
            <Field label="State" value={formData.registeredState} verified={isVerified('registeredState')} />
            <Field label="PIN / ZIP" value={formData.registeredPincode} verified={isVerified('registeredPincode')} />
            <Field label="Country" value={getCountryName(formData.registeredCountry) || formData.registeredCountry} />
          </dl>
        </div>
        <div className="sm:col-span-2 border-t border-border pt-5">
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.08em] text-primary">Supply address</p>
          <dl className="grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2">
            <Field label="Address" value={formData.supplyAddress} wide />
            <Field label="City" value={formData.supplyCity} />
            <Field label="District" value={formData.supplyDistrict} />
            <Field label="State" value={formData.supplyState} />
            <Field label="PIN / ZIP" value={formData.supplyPincode} />
            <Field label="Country" value={getCountryName(formData.supplyCountry) || formData.supplyCountry} />
          </dl>
        </div>
      </Section>

      <Section
        id="bank"
        icon="Landmark"
        title="Bank account"
        meta={bank?.verified_at ? BANK_METHOD[bank.method] || 'Verified with your bank' : undefined}
        stepId={has('bank') ? 'bank' : null}
        onEdit={onEditStep}
        flagged={flaggedSteps.has('bank')}
      >
        <Field label="Bank" value={formData.bankName} verified={isVerified('bankName')} />
        <Field label="Branch" value={formData.branchName} verified={isVerified('branchName')} />
        <Field label="Account number" mono verified={isVerified('accountNumber')}>
          {text(formData.accountNumber) ? (
            <span className="inline-flex items-center gap-2">
              <span>{revealed.account ? formData.accountNumber : maskTail(formData.accountNumber)}</span>
              <button
                type="button"
                onClick={() => toggle('account')}
                className="rounded p-0.5 font-sans text-muted-foreground transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                aria-label={revealed.account ? 'Hide account number' : 'Show account number'}
              >
                <Icon name={revealed.account ? 'EyeOff' : 'Eye'} size={14} />
              </button>
            </span>
          ) : null}
        </Field>
        <Field label="Account holder" value={formData.accountHolderName || bank?.holder_name} verified={Boolean(bank?.verified_at)} />
        <Field label={india ? 'IFSC code' : 'SWIFT / BIC'} value={india ? formData.ifscCode : formData.swiftCode} mono verified={isVerified('ifscCode')} />
        <Field label="Account type" value={labelOf(ACCOUNT_TYPES, formData.accountType)} verified={isVerified('accountType')} />
        <Field label="Bank address" value={formData.bankAddress} verified={isVerified('bankAddress')} wide />
        {bank?.linked && (
          <div className="sm:col-span-2">
            <Badge icon="Link2">PAN linked to this account</Badge>
          </div>
        )}
      </Section>

      <Section
        id={msmeStepId}
        icon={india ? 'Factory' : 'Tags'}
        title={india ? 'MSME & turnover' : 'Categorization'}
        stepId={has(msmeStepId) ? msmeStepId : null}
        onEdit={onEditStep}
        flagged={flaggedSteps.has(msmeStepId)}
      >
        {text(formData.gstTurnoverSlab) && (
          <Field
            label="GST turnover slab"
            value={String(formData.gstTurnoverSlab).replace(/^Slab:\s*/i, '')}
            hint={text(formData.gstTurnoverFy) ? `Financial year ${formData.gstTurnoverFy}` : undefined}
            verified={isVerified('gstTurnoverSlab')}
          />
        )}
        <Field label="Annual turnover" value={formatMoney(formData.annualTurnover, currency)} verified={isVerified('annualTurnover')} />
        <Field label="MSME status" value={labelOf(MSME_STATUS, formData.msmeStatus)} />
        {formData.msmeStatus === 'registered' && (
          <>
            <Field label="Udyam number" value={formData.msmeNumber} mono verified={isVerified('msmeNumber')} />
            <Field label="MSME category" value={labelOf(MSME_CATEGORIES, formData.msmeCategory)} verified={isVerified('msmeCategory')} />
            <Field label="Classification year" value={formData.classificationYear} verified={isVerified('classificationYear')} />
            <Field label="Classification date" value={formatDate(formData.classificationDate)} verified={isVerified('classificationDate')} />
            {udyam?.enterprise_name && <Field label="Enterprise on record" value={udyam.enterprise_name} verified wide />}
          </>
        )}
        {formData.msmeStatus === 'not-registered' && (
          <Field label="Declaration" value={formData.msmeDeclaration ? 'MSME declaration accepted' : null} />
        )}
        <Field label="Vendor type" value={labelOf(VENDOR_TYPES, formData.vendorType)} verified={isVerified('vendorType')} />
        <Field label="Industry sector" value={formData.industrySector} />
        <Field label="Employees" value={formData.employeeCount} />
        {!has('compliance') && <Field label="Preferred currency" value={formData.preferredCurrency} />}
      </Section>

      {has('compliance') && (
        <Section
          id="compliance"
          icon="ScrollText"
          title="Tax & compliance"
          meta={text(formData.constitutionOfBusiness) || undefined}
          stepId="compliance"
          onEdit={onEditStep}
          flagged={flaggedSteps.has('compliance')}
        >
          <Field label="PAN" mono verified={isVerified('panNumber')}>
            {text(formData.panNumber) ? (
              <span className="inline-flex items-center gap-2">
                <span>{revealed.pan ? formData.panNumber : maskTail(formData.panNumber)}</span>
                <button
                  type="button"
                  onClick={() => toggle('pan')}
                  className="rounded p-0.5 font-sans text-muted-foreground transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  aria-label={revealed.pan ? 'Hide PAN' : 'Show PAN'}
                >
                  <Icon name={revealed.pan ? 'EyeOff' : 'Eye'} size={14} />
                </button>
              </span>
            ) : null}
          </Field>
          <Field label="GSTIN" value={formData.gstNumber} mono verified={isVerified('gstNumber')} />
          <Field
            label="GST status"
            value={formData.gstStatus}
            hint={formatDate(formData.gstRegistrationDate) ? `Registered ${formatDate(formData.gstRegistrationDate)}` : undefined}
            verified={isVerified('gstStatus')}
          />
          <Field label="Nature of assessee" value={labelOf(ASSESSEE, formData.natureOfAssessee)} verified={isVerified('natureOfAssessee')} />
          <Field label="Constitution of business" value={formData.constitutionOfBusiness} verified={isVerified('constitutionOfBusiness')} />
          <Field label="CIN" value={formData.cinNumber} mono verified={isVerified('cinNumber')} />
          <Field label="GTA registration" value={formData.gtaRegistration ? titleCase(formData.gtaRegistration) : null} />
          <Field label="Preferred currency" value={formData.preferredCurrency} />
          {!india && (
            <>
              <Field label="Tax registration number" value={formData.taxRegistrationNumber} mono />
              <Field label="VAT number" value={formData.vatNumber} mono />
              <Field label="Business license" value={formData.businessLicense} />
            </>
          )}
          {identity?.mca?.directors?.length > 0 && (
            <div className="sm:col-span-2">
              <dt className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">Directors on MCA record</dt>
              <dd className="mt-2 flex flex-wrap gap-2">
                {identity.mca.directors.map((d) => (
                  <span key={d.din} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted px-2.5 py-1 text-xs text-foreground">
                    <Icon name="User" size={12} className="text-muted-foreground" />
                    {d.name}
                  </span>
                ))}
              </dd>
            </div>
          )}
        </Section>
      )}

      {/* ------------------------------------------------------- documents */}
      <section id="review-documents" aria-labelledby="review-documents-title" className="scroll-mt-24 overflow-hidden rounded-xl border border-border bg-card shadow-xs">
        <header className="flex items-center gap-3 border-b border-border bg-muted/40 px-4 py-3 sm:px-5">
          <span className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
            <Icon name="Paperclip" size={18} />
          </span>
          <div className="min-w-0 flex-1">
            <h3 id="review-documents-title" className="text-sm font-semibold text-foreground">Documents</h3>
            <p className="truncate text-xs text-muted-foreground">
              {documents.filter((d) => d.present).length} of {documents.length} attached
            </p>
          </div>
        </header>
        <ul className="divide-y divide-border">
          {documents.map((doc) => (
            <li key={doc.key} className="flex items-center gap-3 px-4 py-3 sm:px-5">
              <span
                className={`grid h-8 w-8 flex-shrink-0 place-items-center rounded-md ${
                  doc.present ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning'
                }`}
              >
                <Icon name={doc.present ? 'FileCheck' : 'FileWarning'} size={16} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-foreground">{doc.label}</p>
                <p className="truncate text-xs text-muted-foreground">
                  {doc.present ? `${doc.name}${doc.source ? ` · ${doc.source}` : ''}` : 'Not attached yet'}
                </p>
              </div>
              {doc.present && doc.previewable && onPreviewDocument ? (
                <button
                  type="button"
                  onClick={() => onPreviewDocument(doc.handle, doc.type)}
                  className="inline-flex flex-shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-primary transition-colors hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  <Icon name="Eye" size={13} /> Preview
                </button>
              ) : (
                !doc.present && (
                  <button
                    type="button"
                    onClick={() => onEditStep(stepIds.has(doc.step) ? doc.step : msmeStepId)}
                    className="inline-flex flex-shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-primary transition-colors hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    <Icon name="Upload" size={13} /> Add
                  </button>
                )
              )}
            </li>
          ))}
        </ul>
      </section>

      {/* ------------------------------------------------------ agreements */}
      <Section
        id="agreements"
        icon="FileSignature"
        title="Agreements & signature"
        meta={`${acceptedCount} of ${agreements.length} accepted`}
        stepId="agreements"
        onEdit={onEditStep}
        flagged={flaggedSteps.has('agreements')}
      >
        <ul className="sm:col-span-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
          {agreements.map((a) => {
            const ok = Boolean(formData.agreements?.[a.id]);
            return (
              <li
                key={a.id}
                className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
                  ok ? 'border-success/25 bg-success/5 text-foreground' : 'border-warning/40 bg-warning/5 text-foreground'
                }`}
              >
                <Icon name={ok ? 'CheckCircle2' : 'Circle'} size={16} className={ok ? 'flex-shrink-0 text-success' : 'flex-shrink-0 text-warning'} />
                <span className="min-w-0 truncate">{a.title}</span>
              </li>
            );
          })}
        </ul>
        <div className="sm:col-span-2 border-t border-border pt-5">
          <dt className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">Digital signature</dt>
          <dd className="mt-2">
            {signatureUrl ? (
              <img
                src={signatureUrl}
                alt="Your digital signature"
                className="h-16 max-w-[240px] rounded-md border border-border bg-white object-contain p-2"
              />
            ) : uploadedFiles.DIGITAL_SIGNATURE || keepExistingDocuments.digitalSignature ? (
              <Badge icon="PenLine">Signature attached</Badge>
            ) : (
              <span className="text-sm text-muted-foreground/70">{DASH}</span>
            )}
          </dd>
        </div>
      </Section>

      {/* ----------------------------------------------------- declaration */}
      <div
        className={`rounded-xl border-2 p-5 transition-colors sm:p-6 ${
          errors.finalConfirmation
            ? 'border-destructive/50 bg-destructive/5'
            : confirmed
              ? 'border-success/40 bg-success/5'
              : 'border-primary/30 bg-primary/5'
        }`}
      >
        <label
          htmlFor={fieldDomId('finalConfirmation')}
          className="flex cursor-pointer items-start gap-3 rounded-lg p-1 transition-colors"
        >
          <input
            {...fieldProps('finalConfirmation')}
            type="checkbox"
            checked={confirmed}
            onChange={(e) => updateFormData({ finalConfirmation: e.target.checked })}
            className="mt-0.5 h-5 w-5 flex-shrink-0 cursor-pointer rounded border-2 border-primary text-primary focus:ring-2 focus:ring-primary focus:ring-offset-2"
          />
          <span className="min-w-0">
            <span className={`block text-[15px] font-semibold ${errors.finalConfirmation ? 'text-destructive' : 'text-foreground'}`}>
              I confirm that everything above is accurate and complete.
            </span>
            <span className="mt-1 block text-sm text-muted-foreground">
              Amber Enterprises will rely on these details — including the verified registry, bank and MSME information — to onboard my business.
            </span>
            {errors.finalConfirmation && (
              <span className="mt-2 flex items-center gap-1.5 text-sm text-destructive">
                <Icon name="AlertCircle" size={14} />
                {errors.finalConfirmation}
              </span>
            )}
          </span>
        </label>

        <div className="mt-5 grid grid-cols-1 gap-3 border-t border-border/70 pt-5 sm:grid-cols-3">
          {[
            { icon: 'Send', title: 'You submit', body: 'Your application is locked and time-stamped.' },
            { icon: 'UserCheck', title: 'We review', body: 'Our vendor team checks the details and documents.' },
            { icon: 'Mail', title: 'You hear back', body: `We write to ${text(formData.email) || 'your registered email'}.` },
          ].map((s, i) => (
            <div key={s.title} className="flex items-start gap-2.5">
              <span className="grid h-7 w-7 flex-shrink-0 place-items-center rounded-full bg-card text-xs font-semibold text-primary shadow-xs ring-1 ring-border">
                {i + 1}
              </span>
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">{s.title}</p>
                <p className="text-xs leading-relaxed text-muted-foreground">{s.body}</p>
              </div>
            </div>
          ))}
        </div>

        <p className="mt-4 flex items-start gap-2 text-xs text-muted-foreground">
          <Icon name="Lock" size={13} className="mt-0.5 flex-shrink-0" />
          Your details are encrypted in transit and at rest.
        </p>
      </div>
    </div>
  );
};

export default ReviewStep;
